import React from "react";
import { styled, createTheme } from "@mui/material/styles";
import {
  Button,
  Fab,
  TableCell,
  TableRow,
  TextField,
  Select,
  Table,
  InputLabel,
  Checkbox,
  Autocomplete,
  Paper,
  Tooltip,
  Grid,
  TableContainer,
  Box,
  Modal,
  IconButton,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { tableCellClasses } from "@mui/material/TableCell";
import { tooltipClasses } from "@mui/material/Tooltip";
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Icons } from "./Utils/iconsRegistry";

const theme = createTheme({
  palette: {
    defaultColor: {
      main: "#21cdc0",
    },
    white: {
      main: "#FFFFFF",
    },
    apple: {
      main: "#5DBA40",
    },
    steelBlue: {
      main: "#5C76B7",
    },
    violet: {
      main: "#BC00A3",
    },
    hover: {
      main: "#e1f6fa",
    },
    grey: {
      main: "#808080",
    },
    border: {
      main: "#bae0dd",
    },
    cancelAppointment: {
      main: "#848e9f",
    },
    completeAppointment: {
      main: "#ccf7f2",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          color: "#fff", // Set text color to black for contained buttons
        },
      },
    },
  },
});

const StyledSuggestionButton = styled(Button)(
  ({ theme, textColor, hoverBgColor, hoverTextColor }) => ({
    textTransform: "none",
    padding: "6px",
    fontSize: "0.75rem",
    fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
    "&.MuiButton-contained": {
      color: textColor || "#fff",
    },
    ...(hoverBgColor && hoverTextColor
      ? {
          "&:hover": {
            backgroundColor: hoverBgColor,
            color: hoverTextColor,
          },
        }
      : {}),
  })
);

const StyledSelect = styled(Select)(({ theme }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#125386",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
    fontSize: "12px", // Default font size for label
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#125386",
  },
  "&.Mui-focused": {
    "& .MuiInputLabel-root": {
      color: "#125386", // Color when focused
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "12px", // Font size for input value
    padding: "14px !important", // Adjust padding as needed
  },
}));

// const StyledButton = styled(Button)(
//   ({
//     theme,
//     textColor,
//     bgColor,
//     hoverBgColor,
//     hoverTextColor,
//     borderColor,
//     hoverBorderColor,
//   }) => ({
//     textTransform: "none",
//     padding: "6px",
//     fontSize: "0.75rem",
//     fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
//     border: `1px solid ${borderColor || "transparent"}`, // default or custom border
//     "&.MuiButton-contained": {
//       backgroundColor: bgColor || theme.palette.primary.main,
//       color: textColor || "#fff",
//     },
//     ...(hoverBgColor || hoverTextColor || hoverBorderColor
//       ? {
//           "&:hover": {
//             backgroundColor:
//               hoverBgColor || bgColor || theme.palette.primary.dark,
//             color: hoverTextColor || textColor || "#fff",
//             borderColor: hoverBorderColor || borderColor || "transparent", // hover border
//           },
//         }
//       : {}),
//   })
// );

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    ![
      "textColor",
      "bgColor",
      "hoverBgColor",
      "hoverTextColor",
      "borderColor",
      "hoverBorderColor",
      "backgroundColor",
    ].includes(prop),
})(
  ({
    theme,
    textColor,
    bgColor,
    backgroundColor,
    hoverBgColor,
    hoverTextColor,
    borderColor,
    hoverBorderColor,
  }) => ({
    textTransform: "none",
    padding: "6px",
    fontSize: "0.75rem",
    fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
    border: `1px solid ${borderColor || "transparent"}`,

    // Default contained style
    "&.MuiButton-contained": {
      backgroundColor: bgColor || backgroundColor || theme.palette.primary.main,
      color: textColor || "#fff",
    },

    // Hover style
    ...(hoverBgColor || hoverTextColor || hoverBorderColor
      ? {
          "&:hover": {
            backgroundColor:
              hoverBgColor ||
              bgColor ||
              backgroundColor ||
              theme.palette.primary.dark,
            color: hoverTextColor || textColor || "#fff",
            borderColor: hoverBorderColor || borderColor || "transparent",
          },
        }
      : {}),

    // Disabled style
    "&.Mui-disabled": {
      backgroundColor: "#e0e0e0 !important", // grey background
      color: "#9e9e9e !important", // grey text
      borderColor: "#e0e0e0 !important", // grey border if needed
      cursor: "not-allowed",
    },
  })
);

const StyledSelectForHeader = styled(Select)(({ theme }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  backgroundColor: "#f7fdf9", // Light greenish background
  borderRadius: "12px", // Rounded corners
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)", // Subtle shadow
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none", // Remove default outline
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    fontWeight: 600,
    color: "#142c86", // Dark blue text
    padding: "12px 16px !important", // Consistent padding
  },
  "& .MuiSvgIcon-root": {
    color: "#142c86", // Dark blue arrow
  },
}));

const StyledSelectForPastRecords = styled(Select)(({ theme }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  borderRadius: "8px",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #125386",
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    fontWeight: 600,
    padding: "12px 16px !important",
  },
  "& .MuiSvgIcon-root": {
    color: "#142c86",
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  fontSize: "12px", // Default font size
  "&.MuiInputLabel-shrink": {
    fontSize: "1rem", // Font size when the label is shrunk (usually when there's content or focused)
  },
  "&.Mui-focused": {
    color: "#125386", // Color when focused
    fontSize: "1rem", // Font size when focused
  },
}));

// const StyledTextField = styled(TextField)(({ theme }) => ({
//   fontSize: "12px",
//   "& .MuiInputBase-input": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "12px",
//     padding: "16px 14px",
//   },
//   "& .MuiInputLabel-root": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "12px",
//     "&.MuiInputLabel-shrink": {
//       fontSize: "1rem",
//     },
//   },
//   "& .MuiInputLabel-root.Mui-focused": {
//     color: "#21cdc0",
//     fontSize: "1rem",
//   },
//   "& .MuiOutlinedInput-root": {
//     "& .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#bae0dd",
//     },
//     "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#21cdc0",
//     },
//   },
//   "& .MuiSelect-select": {
//     height: "1.4375em", // Remove height: auto
//     padding: "16px 14px", // Adjust padding for better alignment
//   },
// }));

// const StyledMultilineTextField = styled(TextField)(({ theme }) => ({
//   fontSize: "12px",
//   "& .MuiInputBase-input": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "12px", // Set font size for the input value
//   },
//   "& .MuiInputLabel-root": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "12px",
//     "&.MuiInputLabel-shrink": {
//       fontSize: "1rem", // Change font size when label is shrinked (when input has content)
//     },
//   },
//   "& .MuiInputLabel-root.Mui-focused": {
//     color: "#21cdc0",
//     fontSize: "1rem",
//   },
//   "& .MuiOutlinedInput-root": {
//     "& .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#bae0dd",
//     },
//     "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#21cdc0",
//     },
//   },
// }));

// const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
//   fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
//   width: "100%",
//   "& input": {
//     fontSize: "12px", // Font size for input value
//     padding: "16px 14px", // Adjust padding as needed
//   },
//   "& .MuiInputLabel-root": {
//     fontSize: "12px", // Default font size for label
//     "&.MuiInputLabel-shrink": {
//       fontSize: "1rem", // Font size when label is shrinked (when value is present)
//     },
//   },
//   "& .MuiInputLabel-root.Mui-focused": {
//     fontSize: "1rem",
//     color: "#21cdc0",
//   },
//   "& .MuiOutlinedInput-root": {
//     "& .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#bae0dd",
//     },
//     "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#21cdc0",
//     },
//   },
// }));

const StyledTextField = styled(TextField)(({ theme }) => ({
  fontSize: "12px",
  "& .MuiInputBase-input": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "12px",
    padding: "16px 14px",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "12px",
    color: "#9e9e9e", // placeholder/label light gray
    "&.MuiInputLabel-shrink": {
      fontSize: "1rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#125386", // label color on focus
    fontSize: "1rem",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", // Figma border radius
    backgroundColor: "#FDFDFD", // Figma background
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // Figma border color
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // keep same as Figma (optional lighter hover if needed)
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // focus border
      // boxShadow: "0 0 4px #a8d5ff", // subtle glow (keep if allowed in design)
    },
  },
  "& .MuiSelect-select": {
    height: "1.4375em",
    padding: "16px 14px",
  },
}));

const StyledMultilineTextField = styled(TextField)(({ theme }) => ({
  fontSize: "12px",
  "& .MuiInputBase-input": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "12px",
    padding: "16px 14px", // spacing like single line input
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "12px",
    color: "#9e9e9e", // placeholder/label light gray
    "&.MuiInputLabel-shrink": {
      fontSize: "1rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#125386",
    fontSize: "1rem",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", // Figma border radius
    backgroundColor: "#FDFDFD", // Figma background
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // Figma border
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // keep same as figma (or lighten if needed)
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // focus border
      boxShadow: "0 0 4px #a8d5ff", // subtle glow (optional)
    },
  },
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
  width: "100%",
  "& input": {
    fontSize: "12px",
    padding: "16px 14px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "12px",
    color: "#9e9e9e",
    "&.MuiInputLabel-shrink": {
      fontSize: "1rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    fontSize: "1rem",
    color: "#125386",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", // Figma border radius
    backgroundColor: "#FDFDFD", // Figma background
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386", // Figma border
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386",
      boxShadow: "0 0 4px #a8d5ff",
    },
  },
}));

// const StyledTextFieldBootstrap = styled(TextField)(({ theme }) => ({
//   fontSize: "14px",
//   height: "45px",
//   marginBottom: "11px",
//   "& .MuiInputBase-input": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "14px",
//     padding: "16px 14px",
//   },
//   "& .MuiInputLabel-root": {
//     fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
//     fontSize: "12px",
//     "&.MuiInputLabel-shrink": {
//       fontSize: "1rem",
//     },
//   },
//   "& .MuiInputLabel-root.Mui-focused": {
//     color: "#21cdc0",
//     fontSize: "1rem",
//   },
//   "& .MuiOutlinedInput-root": {
//     height: "50px",
//     padding: "6px",
//     "& .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#bae0dd",
//       borderWidth: "2px",
//     },
//     "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#21cdc0",
//     },
//     "&:hover .MuiOutlinedInput-notchedOutline": {
//       borderColor: "#bae0dd", // Keep border color same as default on hover
//     },
//   },
//   "&:hover": {
//     backgroundColor: "transparent",
//   },
//   "&.Mui-error .MuiOutlinedInput-notchedOutline": {
//     borderColor: "#bae0dd",
//     borderWidth: "2px",
//   },
//   "&:hover .MuiOutlinedInput-notchedOutline": {
//     borderColor: "#bae0dd",
//   },

//   "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
//     borderColor: "#bae0dd",
//     borderWidth: "2px",
//   },
//   "& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input": {
//     color: "rgba(0, 0, 0, 0.87)",
//   },
// }));
const StyledTextFieldBootstrap = styled(TextField)(({ theme }) => ({
  fontSize: "14px",
  height: "45px",
  marginBottom: "11px",
  "& .MuiInputBase-input": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "14px",
    padding: "16px 14px",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Roboto,Verdana,Source Sans Pro, sans-serif",
    fontSize: "12px",
    "&.MuiInputLabel-shrink": {
      fontSize: "1rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#125386",
    fontSize: "1rem",
  },
  "& .MuiOutlinedInput-root": {
    height: "50px",
    padding: "6px",
    borderRadius: "10px", // 🔹 Add border radius here
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386",
      borderWidth: "1px",
      borderRadius: "10px", // 🔹 Also add here for consistency
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#125386",
    },
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#125386",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#125386",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#125386",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input": {
    color: "rgba(0, 0, 0, 0.87)",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.defaultColor.main,
    color: theme.palette.common.white,
    fontSize: 30,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 24,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.hover.main,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// const StyledTableCellHistory = styled(TableCell)(({ theme, bgColor }) => ({
//   [`&.${tableCellClasses.head}`]: {
//     backgroundColor: bgColor || theme.palette.defaultColor.main,
//     color: theme.palette.common.white,
//     fontSize: 12,
//   },
//   [`&.${tableCellClasses.body}`]: {
//     fontSize: 10,
//     fontWeight: "bold",
//   },
//   padding: "6px", // Adjust padding as needed
// }));

// const StyledTableRowHistory = styled(TableRow)(({ theme }) => ({
//   "&:nth-of-type(odd)": {
//     backgroundColor: theme.palette.action.hover,
//   },
//   // hide last border
//   "&:last-child td, &:last-child th": {
//     border: 0,
//   },
//   padding: "6px", // Adjust padding as needed
// }));

const StyledTableCellHistory = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#0E588E",
    color: theme.palette.common.white,
    fontSize: 13,
    fontWeight: 600,
    border: "none", // remove borders from header
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    fontWeight: 500,
    color: "#000", // black text like screenshot
    borderBottom: "1px solid #E0E0E0", // subtle divider
  },
  padding: "8px 12px",
}));
const StyledTableCellHistoryForPreview = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#EEEEEE",
    color: "#4D4D4D",
    fontSize: 13,
    fontWeight: 600,
    border: "none", // remove borders from header
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    fontWeight: 500,
    color: "#000", // black text like screenshot
    borderBottom: "1px solid #E0E0E0", // subtle divider
  },
  padding: "8px 12px",
}));

const StyledTableRowHistory = styled(TableRow)(() => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  margin: "10px 5px",
  border: "1px solid #176BAD",
  borderRadius: "10px",
  overflow: "hidden", // ensures header corners are visible
  boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
  overflowX: "auto",
}));

const OutlinedFab = styled(Fab)(({ theme, variant }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  backgroundColor:
    variant === "contained" ? theme.palette.defaultColor.main : "transparent",
  border:
    variant === "outlined"
      ? `2px solid ${theme.palette.defaultColor.main}`
      : "none",
  color:
    variant === "outlined"
      ? theme.palette.defaultColor.main
      : theme.palette.common.white,
  "&:hover": {
    backgroundColor:
      variant === "contained" ? theme.palette.defaultColor.main : "transparent",
    border:
      variant === "outlined"
        ? `2px solid ${theme.palette.defaultColor.main}`
        : "none",
    color:
      variant === "outlined"
        ? theme.palette.defaultColor.main
        : theme.palette.common.white,
  },
}));

const OutlinedIconFab = styled(Fab)(({ theme, variant }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  backgroundColor:
    variant === "contained" ? theme.palette.defaultColor.main : "transparent",
  border:
    variant === "outlined"
      ? `2px solid ${theme.palette.defaultColor.main}`
      : "none",
  color:
    variant === "outlined"
      ? theme.palette.defaultColor.main
      : theme.palette.common.white,

  // 👇 exact circle around icon
  width: "28px", // 20px icon + 4px padding each side
  height: "28px",
  minWidth: "unset",
  minHeight: "unset",
  padding: 0,
  borderRadius: "50%",
  lineHeight: 1,

  "& .MuiSvgIcon-root": {
    fontSize: "20px", // matches your icon
  },

  "&:hover": {
    backgroundColor:
      variant === "contained" ? theme.palette.defaultColor.main : "transparent",
    border:
      variant === "outlined"
        ? `2px solid ${theme.palette.defaultColor.main}`
        : "none",
    color:
      variant === "outlined"
        ? theme.palette.defaultColor.main
        : theme.palette.common.white,
  },
}));

const FabContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  background: "white",
  padding: "5px",
  width: "5%",
  margin: "0px 10px",
});
const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#21cdc0",
  },
}));
const StyledPaper = styled(Paper)(({ theme }) => ({
  fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  fontSize: "12px",
}));

// Reusable StyledAutocomplete component
function StyledAutocomplete(props) {
  return (
    <Autocomplete
      PaperComponent={({ children }) => <StyledPaper>{children}</StyledPaper>}
      {...props}
    />
  );
}

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#21cdc0",
    color: "#21cdc0",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));
const CustomStyledButtonSolid = styled(Button)(({ theme }) => ({
  backgroundColor: "#0d233f", // Button background color
  color: "#fff", // White text color
  border: "none", // No border
  padding: "10px 20px", // Adjusted padding to be more balanced
  cursor: "pointer", // Pointer cursor on hover
  marginLeft: "auto", // Aligns the button to the right, more flexible than using a fixed percentage
  marginRight: "0", // Ensure it's aligned correctly with the right margin
  borderRadius: "5px", // Slightly rounded corners
  width: "fit-content", // Allow the button width to adjust based on content
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0b1c31", // Darker shade for hover effect
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground, // or any grey you want
    color: theme.palette.action.disabled,
    cursor: "not-allowed",
  },
}));
const DoctorconsoleGrid = styled(Grid)(({ theme }) => ({
  // backgroundColor: "#F5FBFF", //
  // fontFamily: "Roboto, Verdana, Source Sans Pro, sans-serif",
  fontSize: "12px",
}));

//Common styled Accordion
const CustomAccordion = styled(MuiAccordion)(({ theme }) => ({
  backgroundColor: "#F4F4F4",
  boxShadow: "none",
  borderRadius: "6px",
  marginBottom: theme.spacing(1),
  border: "1px solid #B1B1B1BB",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(0.5, 0),
    backgroundColor: "#E9FAF9", // whole accordion bg when open
    border: "1px solid #4DD7CD",
  },
}));

const CustomAccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== "hasHeaderContent",
})(({ hasHeaderContent }) => ({
  minHeight: "42px",
  padding: "0 12px",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    width: "100%",
    display: "flex",
    flexDirection: hasHeaderContent ? "column" : "row", // key fix
    alignItems: "center",
  },
  "&.Mui-expanded": {
    backgroundColor: "#E9FAF9",
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#2a3c50",
    textAlign: "left",
  },
}));

const CustomAccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "12px 16px",
  backgroundColor: "#E9FAF9", // content bg when open
  borderTop: "1px solid #e0e0e0",

  // remove border when parent accordion is expanded
  ".Mui-expanded &": {
    borderTop: "none",
  },
}));

const StyledAccordion = ({
  title,
  actions,
  headerContent,
  children,
  expanded,
  onChange,
  ...props
}) => (
  <CustomAccordion expanded={expanded} onChange={onChange} {...props}>
    <CustomAccordionSummary
      expandIcon={<ExpandMoreIcon />}
      hasHeaderContent={!!headerContent} //pass boolean
    >
      {/* Row 1: title + actions + expand icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography sx={{ flex: 1 }}>{title}</Typography>
        {actions && <Box sx={{ mr: 2 }}>{actions}</Box>}
      </Box>

      {/* Row 2: headerContent (table, spans full width) */}
      {headerContent ? <Box sx={{ width: "97%" }}>{headerContent}</Box> : null}
    </CustomAccordionSummary>

    <CustomAccordionDetails>{children}</CustomAccordionDetails>
  </CustomAccordion>
);

const InnerCustomAccordion = styled(MuiAccordion)(({ theme }) => ({
  backgroundColor: "#BADEFB", // entire accordion default
  boxShadow: "none",
  borderRadius: "6px",
  marginBottom: theme.spacing(1),
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(0.5, 0),
    backgroundColor: "transparent", // keep title bar color intact
  },
}));

const InnerCustomAccordionSummary = styled(MuiAccordionSummary)(() => ({
  minHeight: "42px",
  padding: "0 12px",
  backgroundColor: "#BADEFB", // keep the title bar color fixed
  "& .MuiAccordionSummary-content": {
    margin: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  "&.Mui-expanded": {
    backgroundColor: "#BADEFB", // don't change title bar when expanded
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#121212",
    flex: 1, // title takes remaining space
  },
}));

const InnerCustomAccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "12px 16px",
  backgroundColor: "#E9FAF9", // body background changes on expand
  borderTop: "1px solid #e0e0e0",
}));

const InnerStyledAccordion = ({
  title,
  children,
  expanded,
  onChange,
  headerContent,
  detailsSx,
  ...props
}) => (
  <InnerCustomAccordion expanded={expanded} onChange={onChange} {...props}>
    <InnerCustomAccordionSummary expandIcon={<ExpandMoreIcon />}>
      {/* <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}> */}
      <Typography
        sx={{
          color: "#121212",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {/* {headerContent}
      </Box> */}
    </InnerCustomAccordionSummary>
    <InnerCustomAccordionDetails sx={detailsSx}>
      {children}
    </InnerCustomAccordionDetails>
  </InnerCustomAccordion>
);

const UnderwriterCustomAccordion = styled(MuiAccordion)(({ theme }) => ({
  backgroundColor: "white", // body background color (collapsed state)
  boxShadow: "none", // remove shadow
  borderRadius: "6px",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(0.5, 0), // adjust margin for the expanded state
    backgroundColor: "transparent", // no background color for the expanded state
  },
}));

const UnderwriterCustomAccordionSummary = styled((props) => {
  const { onChange, expanded, ...rest } = props;

  const handleClick = (e) => {
    if (e.target.closest("input,button,textarea,select,svg")) return;

    if (typeof onChange === "function") onChange(e, !expanded);
  };

  return <MuiAccordionSummary {...rest} onClick={handleClick} />;
})(({ theme }) => ({
  minHeight: "42px", // consistent header height
  backgroundColor: "#E9F5FE", // light blue background
  borderRadius: "4px", // Slightly rounded border for the header
  "& .MuiAccordionSummary-content": {
    margin: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  "&.Mui-expanded": {
    backgroundColor: "#BADEFB", // keep the light blue background in the expanded state
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#176BAD", // Dark blue text color for the title
    flex: 1,
  },
}));

const UnderwriterCustomAccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "12px 16px",
  backgroundColor: "white", // white background for expanded content
  borderTop: "1px solid #e0e0e0", // light border separating content
}));

const UnderwriterStyledAccordion = ({
  title,
  children,
  expanded,
  onChange,
  headerContent,
  ...props
}) => (
  <UnderwriterCustomAccordion
    expanded={expanded}
    onChange={onChange}
    {...props}
  >
    <UnderwriterCustomAccordionSummary
      expandIcon={<ExpandMoreIcon />}
      onChange={onChange}
      expanded={expanded}
    >
      {headerContent}
    </UnderwriterCustomAccordionSummary>

    <UnderwriterCustomAccordionDetails>
      {children}
    </UnderwriterCustomAccordionDetails>
  </UnderwriterCustomAccordion>
);

const StyledModal = ({ open, handleClose, title, children, disableBackdropClose }) => {
  return (
    <Modal open={open} onClose={(event, reason) => {
      if (disableBackdropClose && reason === "backdropClick") return;
      handleClose(event, reason);
    }} style={{ overflowY: "auto" }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "80%", sm: "60%", md: "60%" },
          maxWidth: "900px",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#fff",
          borderRadius: "12px",
          // p: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {/* Modal Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            // backgroundImage: `url(${Icons.modal_blue})`,
            backgroundColor: "#213360",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          {title && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "20px",
                color: "#ffff",
                mb: 1,
                p: { xs: 1, sm: 2, md: 3 },
              }}
            >
              {title}
            </Typography>
          )}
          <IconButton onClick={handleClose}>
            <CloseIcon
              sx={{
                color: "#FFFFFF",
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
              }}
            />
          </IconButton>
        </Box>

        {/* Modal Body */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            backgroundColor: "#ECECEC",
            borderTop: "1px solid #B1B1B1",
            boxShadow: "0px 4px 6px -2px #00000040",
            px: 0,
            py: 0,
          }}
        >
          <Card
            sx={{
              backgroundColor: "#FDFDFD",
              borderRadius: "0px",
              width: "100%",
            }}
          >
            {children}
          </Card>
        </Box>
      </Box>
    </Modal>
  );
};

export {
  theme,
  StyledButton,
  StyledSelect,
  StyledTextField,
  StyledMultilineTextField,
  StyledTableCell,
  StyledTableCellHistory,
  StyledTableCellHistoryForPreview,
  StyledTableRowHistory,
  StyledTableRow,
  OutlinedFab,
  FabContainer,
  StyledInputLabel,
  StyledDatePicker,
  StyledCheckbox,
  StyledTextFieldBootstrap,
  StyledAutocomplete,
  StyledTooltip,
  CustomStyledButtonSolid,
  DoctorconsoleGrid,
  StyledAccordion,
  InnerStyledAccordion,
  UnderwriterStyledAccordion,
  StyledSuggestionButton,
  StyledTableContainer,
  OutlinedIconFab,
  StyledSelectForPastRecords,
  StyledSelectForHeader,
  StyledModal,
};
